import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Edit, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";

const AdminTeams = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    short_name: "",
    logo_url: "",
    founded: "",
    stadium: "",
    description: "",
    colors: "",
    website: "",
    facebook_url: "",
    instagram_url: "",
    x_url: "",
    team_photo_url: ""
  });

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        founded: formData.founded ? parseInt(formData.founded) : null
      };

      if (editingTeam) {
        await axios.put(`${API}/teams/${editingTeam.team_id}`, payload, { withCredentials: true });
        toast.success("Team updated successfully");
      } else {
        try {
          await axios.post(`${API}/teams`, payload, { withCredentials: true });
          toast.success("Team created successfully");
        } catch (err) {
          // Backend returns 409 with clash list when a near-duplicate name/short_name exists
          if (err.response?.status === 409 && err.response?.data?.detail?.clashes) {
            const clashes = err.response.data.detail.clashes;
            const names = clashes.map(c => `• ${c.name} (${c.reason})`).join("\n");
            if (window.confirm(`A similar team already exists:\n\n${names}\n\nCreate anyway?`)) {
              await axios.post(`${API}/teams?force=true`, payload, { withCredentials: true });
              toast.success("Team created (forced)");
            } else {
              return; // user cancelled — stop here, don't reset form
            }
          } else {
            throw err;
          }
        }
      }

      setIsDialogOpen(false);
      resetForm();
      fetchTeams();
    } catch (error) {
      console.error("Error saving team:", error);
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "Failed to save team");
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name || "",
      short_name: team.short_name || "",
      logo_url: team.logo_url || "",
      founded: team.founded || "",
      stadium: team.stadium || "",
      description: team.description || "",
      colors: team.colors || "",
      website: team.website || "",
      facebook_url: team.facebook_url || "",
      instagram_url: team.instagram_url || "",
      x_url: team.x_url || "",
      team_photo_url: team.team_photo_url || ""
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm("Are you sure you want to delete this team?")) return;
    
    try {
      await axios.delete(`${API}/teams/${teamId}`, { withCredentials: true });
      toast.success("Team deleted successfully");
      fetchTeams();
    } catch (error) {
      console.error("Error deleting team:", error);
      toast.error("Failed to delete team");
    }
  };

  const resetForm = () => {
    setEditingTeam(null);
    setFormData({
      name: "",
      short_name: "",
      logo_url: "",
      founded: "",
      stadium: "",
      description: "",
      colors: "",
      website: "",
      facebook_url: "",
      instagram_url: "",
      x_url: "",
      team_photo_url: ""
    });
  };

  return (
    <AdminLayout title="Teams">
      <div data-testid="admin-teams">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <p className="text-gray-500">Manage all registered teams</p>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="add-team-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Team
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold uppercase">
                  {editingTeam ? "Edit Team" : "Add New Team"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="team-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Team Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="rounded-none"
                      data-testid="team-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Short Name *</label>
                    <Input
                      name="short_name"
                      value={formData.short_name}
                      onChange={handleChange}
                      required
                      className="rounded-none"
                      placeholder="e.g., PAR"
                      data-testid="team-short-name-input"
                    />
                  </div>
                </div>
                <ImageUploader
                  value={formData.logo_url}
                  onChange={(url) => setFormData({ ...formData, logo_url: url })}
                  category="team-logo"
                  label="Team Logo"
                  previewShape="square"
                  testId="team-logo"
                />
                <ImageUploader
                  value={formData.team_photo_url}
                  onChange={(url) => setFormData({ ...formData, team_photo_url: url })}
                  category="team-photo"
                  label="Team Photograph (profile banner)"
                  previewShape="wide"
                  testId="team-photo"
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Founded Year</label>
                    <Input
                      name="founded"
                      type="number"
                      value={formData.founded}
                      onChange={handleChange}
                      className="rounded-none"
                      placeholder="1980"
                      data-testid="team-founded-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Colors</label>
                    <Input
                      name="colors"
                      value={formData.colors}
                      onChange={handleChange}
                      className="rounded-none"
                      placeholder="Blue and White"
                      data-testid="team-colors-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[["website", "Website"], ["facebook_url", "Facebook page"], ["instagram_url", "Instagram"], ["x_url", "X (Twitter)"]].map(([key, label]) => (
                    <div key={key}>
                      <label className="text-sm font-medium">{label}</label>
                      <Input name={key} type="url" value={formData[key]} onChange={handleChange} className="rounded-none" placeholder="https://" data-testid={`team-${key.replace("_", "-")}-input`} />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="text-sm font-medium">Stadium</label>
                  <Input
                    name="stadium"
                    value={formData.stadium}
                    onChange={handleChange}
                    className="rounded-none"
                    data-testid="team-stadium-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="rounded-none"
                    rows={3}
                    data-testid="team-description-input"
                  />
                </div>
                <div className="flex gap-4">
                  <Button type="submit" className="flex-1 bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="team-submit-btn">
                    {editingTeam ? "Update Team" : "Create Team"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-none">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Teams Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1,2,3,4,5,6,7,8].map(i => (
              <div key={i} className="h-48 bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : teams.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {teams.map(team => (
              <div key={team.team_id} className="bg-white border border-gray-200 p-6 text-center group" data-testid={`admin-team-card-${team.team_id}`}>
                <img 
                  src={team.logo_url || 'https://placehold.co/80x80/09090b/FFF?text=TEAM'} 
                  alt={team.name}
                  className="w-20 h-20 mx-auto mb-4 object-contain"
                />
                <h3 className="font-heading font-bold uppercase text-[#09090b]">{team.short_name}</h3>
                <p className="text-sm text-gray-500 mb-4">{team.name}</p>
                <div className="flex gap-2 justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(team)}
                    className="rounded-none"
                    data-testid={`edit-team-${team.team_id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(team.team_id)}
                    className="rounded-none"
                    data-testid={`delete-team-${team.team_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white border border-gray-200">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-gray-400 uppercase mb-2">No Teams Yet</h3>
            <p className="text-gray-500 mb-4">Add your first team to get started.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTeams;
